// The Bloom SDK protocol: how an app's full view (in a sandboxed iframe on the app frame origin) talks to the Bloom
// page hosting it, over postMessage. The frame has no network; this is its only way to anything. The host
// (web/src/apps/bridge.ts) checks every message: it must come from that iframe's window, name a known operation, and
// stay inside the app the frame was opened for (the app is never taken from the message).
import { HUES, type AppManifest, type AppRecord, type Card } from "./apps.ts";
import type { Role } from "./protocol.ts";

export const SDK_VERSION = 1;

/** Operations a frame can ask for, one per SDK function. */
export const SDK_OPS = ["query", "subscribe", "unsubscribe", "call", "currentUser", "members", "postCard", "openApp", "integration", "upload", "media"] as const;

/**
 * Sizes a view can ask for a media file in: "small" (≤400 px wide, for thumbnails), "large" (≤1200 px, to look at) or
 * "original". Videos: "small" and "large" are the poster picture (when there is one), "original" the video itself.
 */
export type MediaSize = "small" | "large" | "original";
/** Uploads one person's views may start per minute. */
export const SDK_UPLOADS_PER_MIN = 20;
export type SdkOp = (typeof SDK_OPS)[number];

/** Frame → host. `ready` (no id) is the handshake: the host answers with `init`. */
export type SdkRequest =
  | { bloom: 1; op: "ready" }
  | { bloom: 1; id: number; op: "query" | "subscribe" | "unsubscribe"; collection: string }
  | { bloom: 1; id: number; op: "call"; action: string; input: Record<string, unknown> }
  | { bloom: 1; id: number; op: "currentUser" | "members" }
  | { bloom: 1; id: number; op: "postCard"; card: Omit<Card, "app"> }
  | { bloom: 1; id: number; op: "openApp"; slug: string; route?: Record<string, string> }
  /** Outside data from one of the integrations the app declares (docs/integrations.md), through the room. */
  | { bloom: 1; id: number; op: "integration"; integration: string; endpoint: string; params: Record<string, unknown> }
  /**
   * Media (docs/apps.md "Media"): `upload` hands the host a picture or video the person picked; the host uploads it
   * through Bloom's normal pipeline and answers with its FileRef, to pass to an action's media input. `media` asks for
   * the bytes of a file in one of this app's records (never any other file): the host fetches it and answers with a
   * Blob, which the frame shows through a blob: URL. The frame itself still has no network.
   */
  | { bloom: 1; id: number; op: "upload"; file: Blob; name: string }
  | { bloom: 1; id: number; op: "media"; key: string; size: MediaSize }
  /**
   * A finger in the frame that may be a swipe between Bloom's panels on a phone (the frame shell's gestures.ts; no
   * id, no answer). Touches inside a cross-origin iframe never reach the host page, so the frame relays drags that
   * start at its left or right edge; the host runs them through the same swipe rules as its own (web/src/mobile.ts
   * SWIPE). x and y are in the frame's own CSS pixels.
   */
  | SdkGesture;

export type SdkGesture = { bloom: 1; op: "gesture"; phase: "start" | "move" | "end"; x: number; y: number };
/** Gesture messages a frame may send per 10 s (a drag sends one per touch move; kept apart from SDK calls' budget). */
export const SDK_GESTURES_PER_WINDOW = 1500;

export interface SdkUser {
  id: string;
  name: string;
  role: Role;
}

export interface SdkMember {
  id: string;
  name: string;
  /** Avatar background (the frame can't load Bloom's avatar images: it has no network). */
  color: string;
}

/** Bloom's theme for the frame: CSS custom properties (the same names as Bloom's) and light or dark. */
export interface SdkTheme {
  scheme: "light" | "dark";
  vars: Record<string, string>;
}

/** Host → frame. */
export type SdkMessage =
  | { bloom: 1; ev: "init"; version: number; app: AppManifest; user: SdkUser; theme: SdkTheme; route: Record<string, string> }
  | { bloom: 1; ev: "theme"; theme: SdkTheme }
  | { bloom: 1; ev: "route"; route: Record<string, string> }
  | { bloom: 1; ev: "change"; collection: string; id: string; record?: AppRecord }
  /** A subscribed collection was reloaded (after a reconnect): replace it. */
  | { bloom: 1; ev: "reset"; collection: string; records: AppRecord[] }
  | { bloom: 1; re: number; ok: true; result: unknown }
  | { bloom: 1; re: number; ok: false; error: string; errors?: Record<string, string>; code?: string };

/** Theme variables the host hands to frames (all from :root in web/src/styles.css). */
export const SDK_THEME_VARS = [
  "--ink", "--ink-2", "--ink-3", "--line", "--line-2", "--field", "--field-hover", "--ground", "--ground-2", "--surface", "--hover",
  "--accent", "--accent-dark", "--accent-soft", "--accent-fill", "--accent-fill-hover", "--accent-line", "--accent-wash", "--on-accent",
  "--online", "--idle", "--danger", "--danger-soft", "--link", "--shade", "--shade-k", "--radius",
  "--tone-positive", "--tone-positive-soft", "--tone-negative", "--tone-negative-soft", "--tone-warning", "--tone-warning-soft",
  "--danger-fill", "--danger-fill-hover",
  // App hues (shared/src/apps.ts HUES): categorical colours and an app's own accent (look.accent).
  ...HUES.flatMap((h) => [`--hue-${h}`, `--hue-${h}-soft`, `--hue-${h}-fill`, `--hue-${h}-fill-hover`]),
];

/**
 * The app iframe's sandbox: scripts and forms only. No allow-same-origin (the frame gets an opaque origin: no
 * cookies, no storage), no top navigation, no popups. The frame page's CSP repeats it (worker/src/frames.ts).
 */
export const FRAME_SANDBOX = "allow-scripts allow-forms";
